import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, MenuItem, TextField, Alert, IconButton, Tooltip, Typography,
  Grid, FormControlLabel, Switch,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { platformTenantHost, platformTenantUrl, PLATFORM_DOMAIN } from '../../constants/brand';
import { ImageUploadField } from '../../components/ui/ImageUploadField';
import { SubscriptionPlanGrid } from '../../components/superadmin/SubscriptionPlanCard';
import { SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton, STATUS_FILTER_OPTIONS } from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { useLocale } from '../../contexts/LocaleContext';
import { RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

type TenantForm = {
  planName: string;
  billingCycle: string;
  price: number;
  gstEnabled: boolean;
  gstRate: number;
  status: string;
  language: string;
  currency: string;
  timeZone: string;
  dateFormat: string;
  subdomain: string;
  companyLogo: string;
};

const emptyForm: TenantForm = {
  planName: '',
  billingCycle: 'monthly',
  price: 0,
  gstEnabled: true,
  gstRate: 18,
  status: 'active',
  language: 'en',
  currency: 'INR',
  timeZone: 'Asia/Kolkata',
  dateFormat: 'DD/MM/YYYY',
  subdomain: '',
  companyLogo: '',
};

export const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [approveTarget, setApproveTarget] = useState<any>(null);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [approvePlanId, setApprovePlanId] = useState('');
  const [approveCycle, setApproveCycle] = useState('monthly');
  const [approveGstEnabled, setApproveGstEnabled] = useState(true);
  const [approveGstRate, setApproveGstRate] = useState(18);
  const [form, setForm] = useState<TenantForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [detailRow, setDetailRow] = useState<any>(null);
  const navigate = useNavigate();
  const { formatDate, formatCurrency, languages, currencies, timezones, dateFormats } = useLocale();

  const activePlans = plans.filter((p) => p.status === 'active');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [t, p] = await Promise.all([superAdminAPI.getTenants(), superAdminAPI.getPlans()]);
      setTenants(t.data);
      setPlans(p.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load tenants'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const planPriceForCycle = (planName: string, cycle: string) => {
    const plan = activePlans.find((p) => p.planName === planName);
    if (!plan) return 0;
    return cycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const openEdit = (tenant: any) => {
    const sub = tenant.subscription || {};
    setEditTarget(tenant);
    setForm({
      planName: sub.planName || tenant.subscriptionPlan?.planName || '',
      billingCycle: sub.billingCycle || 'monthly',
      price: sub.price ?? planPriceForCycle(sub.planName || '', sub.billingCycle || 'monthly'),
      gstEnabled: sub.gstEnabled ?? true,
      gstRate: sub.gstRate ?? 18,
      status: tenant.status === 'suspended' ? 'suspended' : 'active',
      language: tenant.language || 'en',
      currency: tenant.currency || 'INR',
      timeZone: tenant.timeZone || 'Asia/Kolkata',
      dateFormat: tenant.dateFormat || 'DD/MM/YYYY',
      subdomain: tenant.subdomain || '',
      companyLogo: tenant.companyLogo || '',
    });
  };

  const handlePlanChange = (planName: string) => {
    setForm((f) => ({
      ...f,
      planName,
      price: planPriceForCycle(planName, f.billingCycle),
    }));
  };

  const handleCycleChange = (billingCycle: string) => {
    setForm((f) => ({
      ...f,
      billingCycle,
      price: planPriceForCycle(f.planName, billingCycle),
    }));
  };

  const handleApprove = async () => {
    if (!approveTarget || !approvePlanId) return;
    try {
      await superAdminAPI.assignPlan(approveTarget._id, approvePlanId, {
        billingCycle: approveCycle,
        gstEnabled: approveGstEnabled,
        gstRate: approveGstRate,
      });
      setMessage('Tenant approved and plan assigned');
      setApproveTarget(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Approval failed'));
    }
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    try {
      await superAdminAPI.updateTenant(editTarget._id, form);
      setMessage('Tenant subscription updated');
      setEditTarget(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Update failed'));
    }
  };

  const handleQuickStatus = async (tenantId: string, status: string) => {
    try {
      await superAdminAPI.updateTenantStatus(tenantId, status);
      setMessage(`Tenant marked as ${status}`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Status update failed'));
    }
  };

  const formatCycle = (cycle?: string) => cycle === 'yearly' ? 'Yearly' : 'Monthly';

  const buildTenantFields = (t: any): SuperAdminDetailField[] => [
    { label: 'Company', value: t.companyName },
    { label: 'Email', value: t.companyEmail },
    { label: 'Tenant URL', value: t.subdomain ? platformTenantHost(t.subdomain) : '—' },
    { label: 'Plan', value: t.subscription?.planName || '—' },
    { label: 'Billing Cycle', value: formatCycle(t.subscription?.billingCycle) },
    { label: 'Price', value: t.subscription?.price != null ? formatCurrency(t.subscription.price) : '—' },
    { label: 'GST', value: t.subscription?.gstEnabled ? `${t.subscription.gstRate}%` : 'Off' },
    { label: 'Next Billing', value: t.subscription?.nextBillingDate ? formatDate(t.subscription.nextBillingDate) : '—' },
    { label: 'Users', value: t.usage?.users ?? '—' },
    { label: 'Language', value: t.language || 'en' },
    { label: 'Currency', value: t.currency || 'INR' },
    { label: 'Timezone', value: t.timeZone || 'Asia/Kolkata' },
    { label: 'Tenant ID', value: t.tenantId, fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader
        title="Manage Tenants"
        subtitle={getPageSubtitle('tenants')}
        icon={<BusinessIcon />}

      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <SuperAdminDataTable
        rows={tenants}
        loading={loading}
        error={error}
        exportFileName="super-admin-tenants"
        exportSheetName="Tenants"
        statusField="status"
        searchPlaceholder="Search company, email, owner, plan..."
        statusOptions={[
          { label: 'All Status', value: 'all' },
          { label: 'Active', value: 'active' },
          { label: 'Pending', value: 'pending' },
          { label: 'Suspended', value: 'suspended' },
          { label: 'Inactive', value: 'inactive' },
        ]}
        columns={[
          {
            id: 'company',
            header: 'Company',
            searchValue: (t) => `${t.companyName} ${t.companyEmail}`,
            exportValue: (t) => t.companyName,
            render: (t) => <Typography fontWeight={600} fontSize={13}>{t.companyName}</Typography>,
          },
          {
            id: 'email',
            header: 'Email',
            searchValue: (t) => t.companyEmail,
            exportValue: (t) => t.companyEmail,
            render: (t) => t.companyEmail,
          },
          {
            id: 'subdomain',
            header: 'Tenant URL',
            searchValue: (t) => `${t.subdomain || ''} ${t.tenantUrl || ''}`,
            exportValue: (t) => t.tenantUrl || (t.subdomain ? platformTenantUrl(t.subdomain) : ''),
            render: (t) => t.subdomain ? (
              <Typography fontSize={12} fontWeight={600} sx={{ color: '#2563eb' }}>
                {platformTenantHost(t.subdomain)}
              </Typography>
            ) : '—',
          },
          {
            id: 'plan',
            header: 'Plan',
            searchValue: (t) => t.subscription?.planName,
            exportValue: (t) => t.subscription?.planName,
            render: (t) => t.subscription?.planName || '—',
          },
          {
            id: 'cycle',
            header: 'Billing Cycle',
            searchValue: (t) => t.subscription?.billingCycle,
            exportValue: (t) => t.subscription?.billingCycle === 'yearly' ? 'Yearly' : 'Monthly',
            filterType: 'select',
            filterOptions: [{ label: 'Monthly', value: 'monthly' }, { label: 'Yearly', value: 'yearly' }],
            filterValue: (t) => t.subscription?.billingCycle || '',
            render: (t) => formatCycle(t.subscription?.billingCycle),
          },
          {
            id: 'nextBilling',
            header: 'Next Billing',
            searchValue: (t) => t.subscription?.nextBillingDate ? String(t.subscription.nextBillingDate) : '',
            exportValue: (t) => t.subscription?.nextBillingDate,
            render: (t) => t.subscription?.nextBillingDate ? formatDate(t.subscription.nextBillingDate) : '—',
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (t) => t.status,
            filterType: 'select',
            filterOptions: STATUS_FILTER_OPTIONS,
            filterValue: (t) => t.status,
            render: (t) => statusChip(t.status),
          },
          {
            id: 'users',
            header: 'Users',
            searchValue: (t) => t.usage?.users != null ? String(t.usage.users) : '',
            exportValue: (t) => t.usage?.users,
            render: (t) => t.usage?.users ?? '—',
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(t) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            {t.status !== 'pending' && (
              <Tooltip title="Manage module access">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/super-admin/tenant-modules?tenant=${t._id}`); }} color="secondary">
                  <ViewModuleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {t.status !== 'pending' && (
              <AdminTableEditButton onClick={() => openEdit(t)} title="Edit tenant & billing" />
            )}
            {t.status === 'pending' && (
              <Button size="small" variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  setApproveTarget(t);
                  setApprovePlanId('');
                  setApproveCycle('monthly');
                  setApproveGstEnabled(true);
                  setApproveGstRate(18);
                }}
                sx={{ ...catchyPrimaryButtonSx, minWidth: 0, px: 1.5, py: 0.5, fontSize: 12 }}>
                Approve
              </Button>
            )}
            {t.status === 'active' && (
              <Tooltip title="Suspend tenant">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleQuickStatus(t._id, 'suspended'); }} color="error">
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(t.status === 'suspended' || t.status === 'inactive') && (
              <Tooltip title="Activate tenant">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleQuickStatus(t._id, 'active'); }} color="success">
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Tenant"
        title={detailRow?.companyName || ''}
        subtitle={detailRow?.companyEmail}
        status={detailRow?.status}
        fields={detailRow ? buildTenantFields(detailRow) : []}
        primaryActions={detailRow ? (
          <>
            {detailRow.status !== 'pending' && (
              <>
                <Button variant="contained" onClick={() => { openEdit(detailRow); setDetailRow(null); }} sx={catchyPrimaryButtonSx}>
                  Edit Tenant
                </Button>
                <Button variant="outlined" onClick={() => navigate(`/super-admin/tenant-modules?tenant=${detailRow._id}`)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Module Access
                </Button>
              </>
            )}
            {detailRow.status === 'pending' && (
              <Button variant="contained" onClick={() => {
                setApproveTarget(detailRow);
                setApprovePlanId('');
                setApproveCycle('monthly');
                setApproveGstEnabled(true);
                setApproveGstRate(18);
                setDetailRow(null);
              }} sx={catchyPrimaryButtonSx}>
                Approve Tenant
              </Button>
            )}
          </>
        ) : undefined}
      />

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)}
        PaperProps={{ sx: { borderRadius: `${RADIUS}px`, minWidth: 520, maxWidth: 720 } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Approve {approveTarget?.companyName}</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>Select a subscription plan</Typography>
          <SubscriptionPlanGrid
            plans={activePlans}
            formatMoney={formatCurrency}
            selectedPlanId={approvePlanId}
            onSelect={(p) => setApprovePlanId(p._id)}
            compact
          />
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Billing Cycle" value={approveCycle}
                onChange={(e) => setApproveCycle(e.target.value)} sx={catchyTextFieldSx}>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="GST Rate (%)" value={approveGstRate}
                onChange={(e) => setApproveGstRate(Number(e.target.value))}
                disabled={!approveGstEnabled} sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={approveGstEnabled} onChange={(e) => setApproveGstEnabled(e.target.checked)} />}
                label="GST Enabled"
              />
            </Grid>
          </Grid>
          <Button variant="contained" onClick={handleApprove} disabled={!approvePlanId} fullWidth
            sx={{ ...catchyPrimaryButtonSx, mt: 2 }}>
            Approve & Activate
          </Button>
        </Box>
      </Dialog>

      {/* Edit tenant / billing dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)}
        PaperProps={{ sx: { borderRadius: `${RADIUS}px`, minWidth: 520, maxWidth: 760 } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Edit Tenant</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            {editTarget?.companyName} — plan changes reset the billing cycle dates
          </Typography>

          <Typography fontSize={12} fontWeight={700} color="text.secondary" mb={1}>SUBSCRIPTION PLAN</Typography>
          <SubscriptionPlanGrid
            plans={activePlans}
            formatMoney={formatCurrency}
            selectedPlanName={form.planName}
            onSelect={(p) => handlePlanChange(p.planName)}
            compact
          />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Billing Cycle" value={form.billingCycle}
                onChange={(e) => handleCycleChange(e.target.value)} sx={catchyTextFieldSx}>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Price" value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="GST Rate (%)" value={form.gstRate}
                onChange={(e) => setForm((f) => ({ ...f, gstRate: Number(e.target.value) }))}
                disabled={!form.gstEnabled} sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.gstEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, gstEnabled: e.target.checked }))} />}
                label="GST Enabled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Tenant Status" value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                sx={catchyTextFieldSx}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mt={1}>Branding & portal URL</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Subdomain" value={form.subdomain}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                }))}
                helperText={form.subdomain ? platformTenantUrl(form.subdomain) : `e.g. company.${PLATFORM_DOMAIN}`}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Portal URL" value={form.subdomain ? platformTenantUrl(form.subdomain) : ''}
                InputProps={{ readOnly: true }} sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <ImageUploadField label="Company Logo"
                value={form.companyLogo}
                onUpload={async (file) => {
                  const fd = new FormData();
                  fd.append('file', file);
                  fd.append('type', 'logo');
                  const res = await superAdminAPI.uploadBillingAsset(fd);
                  return res.data.url as string;
                }}
                onChange={(url) => setForm((f) => ({ ...f, companyLogo: url }))}
                helperText="Shown in tenant sidebar after save"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize={12} fontWeight={700} color="text.secondary" mt={1}>Regional (tenant locale)</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Language" value={form.language}
                onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))} sx={catchyTextFieldSx}>
                {languages.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Currency" value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} sx={catchyTextFieldSx}>
                {currencies.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Timezone" value={form.timeZone}
                onChange={(e) => setForm((f) => ({ ...f, timeZone: e.target.value }))} sx={catchyTextFieldSx}>
                {timezones.map((tz) => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Date Format" value={form.dateFormat}
                onChange={(e) => setForm((f) => ({ ...f, dateFormat: e.target.value }))} sx={catchyTextFieldSx}>
                {dateFormats.map((df) => <MenuItem key={df.value} value={df.value}>{df.label}</MenuItem>)}
              </TextField>
            </Grid>
            {editTarget?.subscription?.nextBillingDate && (
              <Grid item xs={12}>
                <Typography fontSize={13} color="text.secondary">
                  Next billing date: {formatDate(editTarget.subscription.nextBillingDate)}
                  (updates on plan or cycle change)
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={!form.planName}
              sx={catchyPrimaryButtonSx}>
              Save Changes
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
